<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import {
  api,
  type ContactSettingsDocument,
  type ContactSubmissionDocument,
} from "./api";

const props = defineProps<{ siteId: string }>();
const settings = ref<ContactSettingsDocument | null>(null);
const submissions = ref<ContactSubmissionDocument[]>([]);
const selectedId = ref("");
const activeView = ref<"inbox" | "settings">("inbox");
const statusFilter = ref<"all" | ContactSubmissionDocument["status"]>("all");
const canEdit = ref(false);
const loading = ref(true);
const saving = ref(false);
const changingStatus = ref(false);
const error = ref("");
const notice = ref("");

const filteredSubmissions = computed(() =>
  statusFilter.value === "all"
    ? submissions.value
    : submissions.value.filter((item) => item.status === statusFilter.value),
);
const selected = computed(() =>
  submissions.value.find((item) => item.id === selectedId.value) ?? filteredSubmissions.value[0] ?? null,
);
const newCount = computed(() => submissions.value.filter((item) => item.status === "new").length);

function formatDate(value: string) {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function addTopic() {
  settings.value?.topics.push("");
}

async function saveSettings() {
  if (!settings.value || !canEdit.value) return;
  saving.value = true;
  error.value = "";
  notice.value = "";
  try {
    settings.value = (
      await api.updateContactSettings(props.siteId, settings.value)
    ).settings;
    notice.value = "Contact form settings saved.";
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to save contact settings";
  } finally {
    saving.value = false;
  }
}

async function setStatus(status: ContactSubmissionDocument["status"]) {
  if (!selected.value || !canEdit.value) return;
  changingStatus.value = true;
  error.value = "";
  try {
    const response = await api.updateContactSubmissionStatus(
      props.siteId,
      selected.value.id,
      status,
    );
    Object.assign(selected.value, response.submission);
    notice.value = `Enquiry marked ${status}.`;
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to update the enquiry";
  } finally {
    changingStatus.value = false;
  }
}

async function selectSubmission(submission: ContactSubmissionDocument) {
  selectedId.value = submission.id;
  if (submission.status === "new" && canEdit.value) await setStatus("read");
}

onMounted(async () => {
  try {
    const [settingsResponse, submissionsResponse] = await Promise.all([
      api.contactSettings(props.siteId),
      api.contactSubmissions(props.siteId),
    ]);
    settings.value = settingsResponse.settings;
    canEdit.value = settingsResponse.canEdit;
    submissions.value = submissionsResponse.submissions;
    selectedId.value = submissions.value[0]?.id ?? "";
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to load the contact form";
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <section class="contact-card">
    <div class="module-heading">
      <div>
        <p class="eyebrow">Customer enquiries</p>
        <h2>Contact form</h2>
        <p>Keep website messages together and send new-enquiry notifications by email.</p>
      </div>
      <div class="view-tabs">
        <button type="button" :class="{ active: activeView === 'inbox' }" @click="activeView = 'inbox'">Inbox <span v-if="newCount">{{ newCount }}</span></button>
        <button type="button" :class="{ active: activeView === 'settings' }" @click="activeView = 'settings'">Form settings</button>
      </div>
    </div>

    <p v-if="error" class="message error" role="alert">{{ error }}</p>
    <p v-if="notice" class="message success" role="status">{{ notice }}</p>
    <div v-if="loading" class="loading">Loading contact form…</div>

    <template v-else-if="settings && activeView === 'settings'">
      <div v-if="!settings.recipientEmail" class="message warning">
        Messages will still be saved in the inbox, but email notifications need a recipient address.
      </div>
      <form class="settings-form" @submit.prevent="saveSettings">
        <div class="fields">
          <label>Page heading<input v-model="settings.title" :disabled="!canEdit" maxlength="160"></label>
          <label>Notification email<input v-model="settings.recipientEmail" :disabled="!canEdit" type="email" maxlength="254" placeholder="jackie@example.com"></label>
          <label class="wide">Introduction<textarea v-model="settings.introduction" :disabled="!canEdit" rows="3" maxlength="1000" /></label>
          <label class="wide">Message shown after sending<textarea v-model="settings.successMessage" :disabled="!canEdit" rows="3" maxlength="500" /></label>
          <label class="check"><input v-model="settings.phoneRequired" :disabled="!canEdit" type="checkbox"> Require a telephone number</label>
        </div>
        <section class="topics">
          <div class="section-heading"><div><h3>Enquiry topics</h3><p>Give customers a short subject menu.</p></div><button type="button" :disabled="!canEdit" @click="addTopic">Add topic</button></div>
          <div class="topic-grid">
            <div v-for="(_topic, index) in settings.topics" :key="index" class="topic-row">
              <input v-model="settings.topics[index]" :disabled="!canEdit" maxlength="160">
              <button class="remove" type="button" :disabled="!canEdit" @click="settings.topics.splice(index, 1)">Remove</button>
            </div>
          </div>
        </section>
        <div class="settings-footer"><span>All enquiries are stored even if email delivery is unavailable.</span><button type="submit" :disabled="saving || !canEdit">{{ saving ? "Saving…" : "Save form settings" }}</button></div>
      </form>
    </template>

    <template v-else-if="settings">
      <div class="inbox-toolbar">
        <strong>{{ submissions.length }} {{ submissions.length === 1 ? "enquiry" : "enquiries" }}</strong>
        <label>Show<select v-model="statusFilter"><option value="all">All messages</option><option value="new">New</option><option value="read">Read</option><option value="replied">Replied</option><option value="spam">Spam</option></select></label>
      </div>
      <div v-if="filteredSubmissions.length" class="inbox-layout">
        <nav class="submission-list" aria-label="Contact enquiries">
          <button v-for="submission in filteredSubmissions" :key="submission.id" type="button" :class="{ active: selected?.id === submission.id, unread: submission.status === 'new' }" @click="selectSubmission(submission)">
            <span class="status-dot" :class="submission.status"></span>
            <span><strong>{{ submission.name }}</strong><small>{{ submission.subject || "General enquiry" }}</small><time>{{ formatDate(submission.createdAt) }}</time></span>
          </button>
        </nav>
        <article v-if="selected" class="submission-detail">
          <header><div><p class="eyebrow">{{ selected.status }}</p><h3>{{ selected.subject || "General enquiry" }}</h3></div><time>{{ formatDate(selected.createdAt) }}</time></header>
          <dl><div><dt>From</dt><dd>{{ selected.name }}</dd></div><div><dt>Email</dt><dd><a :href="`mailto:${selected.email}`">{{ selected.email }}</a></dd></div><div><dt>Telephone</dt><dd>{{ selected.telephone || "Not supplied" }}</dd></div></dl>
          <p class="submission-message">{{ selected.message }}</p>
          <div class="submission-actions">
            <a class="reply" :href="`mailto:${selected.email}?subject=${encodeURIComponent(`Re: ${selected.subject || 'Your enquiry'}`)}`">Reply by email</a>
            <button type="button" :disabled="changingStatus || !canEdit" @click="setStatus('replied')">Mark replied</button>
            <button class="remove" type="button" :disabled="changingStatus || !canEdit" @click="setStatus('spam')">Mark spam</button>
          </div>
        </article>
      </div>
      <div v-else class="empty"><strong>No messages here.</strong><span>New website enquiries will appear in this inbox.</span></div>
    </template>
  </section>
</template>

<style scoped>
.contact-card,.settings-form,.topics{display:grid;gap:1.2rem}.contact-card{padding:1.5rem;border:1px solid #d9ddd8;border-radius:1rem;background:#fff}.module-heading,.section-heading,.settings-footer,.inbox-toolbar,.submission-detail header{display:flex;align-items:center;justify-content:space-between;gap:1rem}.module-heading h2,.section-heading h3{margin:0}.module-heading p:last-child,.section-heading p{margin:.35rem 0 0;color:#6d7972}.eyebrow{margin:0 0 .25rem;color:#6b7b72;font-size:.68rem;font-weight:800;letter-spacing:.1em;text-transform:uppercase}.view-tabs{display:flex;padding:.3rem;border-radius:.7rem;background:#eef2ef}.view-tabs button{color:#456055;background:transparent}.view-tabs button.active{color:#fff;background:#285c47}.view-tabs span{display:inline-grid;min-width:1.3rem;height:1.3rem;margin-left:.25rem;place-items:center;border-radius:50%;color:#285c47;background:#fff;font-size:.68rem}.fields{display:grid;grid-template-columns:1fr 1fr;gap:.85rem}.wide{grid-column:1/-1}label{display:grid;gap:.4rem}textarea{width:100%;padding:.75rem;resize:vertical;border:1px solid #cbd5ce;border-radius:.65rem;background:#fff;font:inherit}.check{display:flex;align-items:center}.check input{width:auto}.topics{padding-top:1rem;border-top:1px solid #dce2dc}.section-heading button{padding:.55rem .7rem;color:#365045;background:#e8eeea}.topic-grid{display:grid;grid-template-columns:1fr 1fr;gap:.65rem}.topic-row{display:grid;grid-template-columns:1fr auto;gap:.5rem}.settings-footer{padding-top:1rem;border-top:1px solid #dce2dc;color:#6d7972;font-size:.78rem}.inbox-toolbar{padding:.7rem 0}.inbox-toolbar label{display:flex;align-items:center;gap:.5rem}.inbox-layout{display:grid;min-height:36rem;grid-template-columns:minmax(16rem,.65fr) minmax(0,1.35fr);gap:1rem}.submission-list{display:grid;max-height:45rem;align-content:start;overflow-y:auto;gap:.4rem}.submission-list button{display:grid;padding:.8rem;grid-template-columns:auto 1fr;gap:.65rem;color:#365045;background:#f4f7f4;text-align:left}.submission-list button.active{color:#fff;background:#285c47}.submission-list button.unread strong{font-weight:900}.submission-list strong,.submission-list small,.submission-list time{display:block}.submission-list small{margin-top:.2rem}.submission-list time{margin-top:.45rem;opacity:.68;font-size:.65rem}.status-dot{width:.6rem;aspect-ratio:1;margin-top:.2rem;border-radius:50%;background:#8b9991}.status-dot.new{background:#dd735f}.status-dot.replied{background:#63a475}.status-dot.spam{background:#9d6b65}.submission-detail{display:grid;align-content:start;gap:1.2rem;padding:1.35rem;border:1px solid #dce2dc;border-radius:.8rem;background:#fafbf9}.submission-detail h3{margin:0;font:700 2rem/1 Georgia,serif}.submission-detail header time{color:#6d7972;font-size:.72rem}.submission-detail dl{display:flex;margin:0;flex-wrap:wrap;gap:1.5rem}.submission-detail dl div{display:grid;gap:.2rem}.submission-detail dt{color:#6d7972;font-size:.65rem;font-weight:800;text-transform:uppercase}.submission-detail dd{margin:0;font-size:.84rem}.submission-message{min-height:12rem;margin:0;padding:1.2rem;border-radius:.7rem;background:#fff;line-height:1.7;white-space:pre-wrap}.submission-actions{display:flex;flex-wrap:wrap;gap:.6rem}.submission-actions .reply{padding:.72rem 1rem;border-radius:.6rem;color:#fff;background:#285c47;font-weight:800;text-decoration:none}.remove{color:#91382f!important;background:#fae9e7!important}.message{margin:0;padding:.85rem 1rem;border-radius:.65rem}.message.error{color:#86251f;background:#fee9e7}.message.success{color:#295b40;background:#e2f1e7}.message.warning{color:#765315;background:#fff2cd}.loading,.empty{display:grid;padding:2rem;gap:.35rem;color:#6d7972;text-align:center}@media(max-width:900px){.module-heading{align-items:stretch;flex-direction:column}.view-tabs{align-self:flex-start}.inbox-layout{grid-template-columns:1fr}.submission-list{display:flex;overflow-x:auto}.submission-list button{min-width:15rem}}@media(max-width:650px){.fields,.topic-grid{grid-template-columns:1fr}.wide{grid-column:auto}.section-heading,.settings-footer,.submission-detail header{align-items:stretch;flex-direction:column}.topic-row{grid-template-columns:1fr}.view-tabs{width:100%}.view-tabs button{flex:1}}
</style>
